/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FieldsProps, FormProps } from '@/auth/types';
import { useForm } from '@/auth/hooks/components/useForm';
import { FieldEnumList } from '@/auth/data/models';
import { Slot } from '@adobe-commerce/elsie/lib';
import {
  SelectElement,
  InputElement,
  InputDateElement,
  InputCheckBoxElement,
  TextAreaElement,
  FormSlot,
} from './Elements';
import { memo } from 'preact/compat';

export const Form = memo(
  ({
    slots,
    name,
    loading,
    children,
    className = 'defaultForm',
    fieldsConfig = [],
    onSubmit,
    formSlot,
    ...props
  }: FormProps) => {
    const {
      formData,
      errors,
      formRef,
      handleChange,
      handleBlur,
      handleSubmit,
      handleFocus,
    } = useForm({
      onSubmit,
      fieldsConfig,
    });

    const itemClassName = `${className}__field`;

    const formContent = (
      <>
        {fieldsConfig.map((item: FieldsProps) => {
          const errorConfig = errors?.[item.customUpperCode];
          const valueMessage = formData?.[item.customUpperCode] as string;
          const isSlot = !!slots?.[`SignUpFormInput_${item.code}`];

          const renderSlot = (
            <FormSlot
              slots={slots}
              item={item}
              handleOnChange={handleChange}
              handleOnBlur={handleBlur}
              handleOnFocus={handleFocus}
              errorConfig={errorConfig}
              errors={errors}
            />
          );

          switch (item.fieldType) {
            case FieldEnumList.TEXT: {
              if (isSlot) return renderSlot;

              if (item.options.length) {
                return (
                  <SelectElement
                    item={item}
                    valueMessage={valueMessage}
                    errorConfig={errorConfig}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    itemClassName={itemClassName}
                    className={className}
                    loading={loading}
                  />
                );
              }

              return (
                <InputElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            case FieldEnumList.MULTILINE: {
              if (isSlot) return renderSlot;

              return (
                <InputElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            case FieldEnumList.SELECT: {
              if (isSlot) return renderSlot;

              return (
                <SelectElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            case FieldEnumList.DATE: {
              if (isSlot) return renderSlot;

              return (
                <InputDateElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            case FieldEnumList.BOOLEAN: {
              if (isSlot) return renderSlot;

              return (
                <InputCheckBoxElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            case FieldEnumList.TEXTAREA: {
              if (isSlot) return renderSlot;

              return (
                <TextAreaElement
                  item={item}
                  valueMessage={valueMessage}
                  errorConfig={errorConfig}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  itemClassName={itemClassName}
                  className={className}
                  loading={loading}
                />
              );
            }

            default:
              return null;
          }
        })}
        {children}
      </>
    );

    const slotContext = {
      ...(formSlot?.context ?? {}),
      __formContentKey: [
        fieldsConfig.map((f: FieldsProps) => f.id || f.code).join(','),
        JSON.stringify(errors),
        JSON.stringify(formData),
      ].join('|'),
    };

    return (
      <form
        onSubmit={handleSubmit}
        name={name}
        ref={formRef}
        onFocus={handleFocus}
        {...props}
      >
        {formSlot ? (
          <Slot
            name={formSlot.name}
            slot={formSlot.slot}
            context={slotContext}
            className={className}
            data-testid={formSlot.dataTestId}
          >
            {formContent}
          </Slot>
        ) : formContent }
      </form>
    );
  }
);
